import { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { first, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Store
import { useAppSelector } from 'store';
import { selectUserData } from 'store/slices/userData';
import { selectHeaderPartners } from 'store/slices/header';
// Import Components
import DataTable from 'components/common/DataTable';
import DownloadOrSendToEmailToolbar from 'components/common/DownloadOrSendToEmailToolbar';
// Import Services
import { ExecutedReportHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
// Import Constants
import { AlertTypes, DownloadExtensions, l } from 'constants/common';

let executedReportResultData: AnyObject = {};
let resultDataCount = 0;

const ResultInfoTab = ({ adHocReportResultId, reportType, reportName, adHocReportVersion }) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const { showToaster } = useToaster();

    const userData = useAppSelector(selectUserData);
    const { ActivePartner, Settings } = userData ?? {};
    const partnersList = useAppSelector(selectHeaderPartners);

    const partnerCurrencyId = useMemo(
        () => first(partnersList.filter((partner) => partner.value === ActivePartner?.PartnerId))?.CurrencyId,
        [ActivePartner?.PartnerId, partnersList],
    );

    const [isLoading, setIsLoading] = useState(false);
    const [resultClients, setResultClients] = useState([]);
    const [resultClientsColumns, setResultClientsColumns] = useState([]);
    const [dataCount, setDataCount] = useState(resultDataCount);
    const [isFileLoading, setIsFileLoading] = useState(false);

    const { downloadExecutedReport, sendToEmailExecutedReport, getExecutedReportResultRequest } = useCancelableRequests(
        {
            downloadExecutedReport: ExecutedReportHttpService.downloadExecutedReport,
            sendToEmailExecutedReport: ExecutedReportHttpService.sendToEmailExecutedReport,
            getExecutedReportResultRequest: ExecutedReportHttpService.getExecutedReportResult,
        },
    );

    const mapResultClientsColumns = (columns, t) =>
        columns.map((columns, index) => ({
            index,
            text: t(columns.DisplayName),
            dataKey: index,
            sortable: false,
        }));

    const mapResultClients = (data) =>
        data.Data.map((elem, index) => ({
            index,
            actionBar: false,
            dragDisable: true,
            hasHover: true,
            data: data.Columns.reduce(
                (accumulator, _element, i) => {
                    accumulator[i] = elem[i];
                    return accumulator;
                },
                { index },
            ),
        }));

    const executedReportResult = () => {
        const requestBody = {
            ReportResultId: adHocReportResultId,
            Searchmodel: {
                Filters: [{ Name: 'Name', Comparision: 2, Values: [''] }],
                Pageing: { PageSize: 200, PageNumber: 1 },
                Sorting: null,
            },
            CurrencyCode: ActivePartner?.CurrencyId ?? partnerCurrencyId,
            Type: reportType,
        };

        setIsLoading(true);
        doPostRequest(getExecutedReportResultRequest.request, {
            requestBody,
            successCallback: (Data) => {
                resultDataCount = Data?.Count;
                setDataCount(resultDataCount);
                executedReportResultData = first(Data.Data);
            },
        }).then(() => setIsLoading(false));
    };

    const fileName = `${reportName}_Version_${adHocReportVersion}`;

    const getExportRequestBody = () => ({
        CurrencyCode: Settings?.CurrencyId,
        DocumentType: DownloadExtensions.xlsx,
        ReportType: reportType,
        ReportResultId: adHocReportResultId,
        TopCount: null,
        UserTimeZone: ActivePartner?.TimeZone,
        fileName,
    });

    const downloadClickHandler = () => {
        setIsFileLoading(true);

        doPostRequest(downloadExecutedReport.request, {
            requestBody: getExportRequestBody(),
            successCallback: (data) => {
                window.open(data?.Url);
            },
            isShowError: false,
        }).then(({ AlertType, AlertMessage, IsCanceled }) => {
            // Aborted on unmount: no toaster
            if (IsCanceled) {
                return;
            }
            showToaster(AlertType, AlertMessage);
            setIsFileLoading(false);
        });
    };

    const sendToEmailClickHandler = () => {
        setIsFileLoading(true);

        doPostRequest(sendToEmailExecutedReport.request, {
            requestBody: getExportRequestBody(),
            successCallback: (_data, AlertMessage) => {
                showToaster(
                    AlertTypes.success,
                    isEmpty(AlertMessage) ? t(l.ReportResultWillBeSentToYourEmail) : AlertMessage,
                );
            },
        }).then(() => setIsFileLoading(false));
    };

    const getHeaderActions = () => {
        return (
            <DownloadOrSendToEmailToolbar
                downloadAction={downloadClickHandler}
                sendToEmailAction={sendToEmailClickHandler}
                isFileLoading={isFileLoading}
                isDisabledDownload={isEmpty(executedReportResultData)}
            />
        );
    };

    useEffect(() => {
        executedReportResult();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, []);

    useEffect(() => {
        if (!isEmpty(executedReportResultData)) {
            setResultClientsColumns(mapResultClientsColumns(executedReportResultData.Columns, t));
            setResultClients(mapResultClients(executedReportResultData));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [executedReportResultData]);

    return (
        <DataTable
            columnKey="dataKey"
            rowKey="index"
            data={resultClients}
            dataCount={dataCount}
            columns={resultClientsColumns}
            isLoading={isLoading}
            withPagination={false}
            withPageSelector={false}
            headerActions={getHeaderActions()}
        />
    );
};

ResultInfoTab.propTypes = {
    adHocReportResultId: PropTypes.number.isRequired,
    reportType: PropTypes.number.isRequired,
    reportName: PropTypes.string.isRequired,
    adHocReportVersion: PropTypes.number.isRequired,
};

export default memo(ResultInfoTab);
